import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Cell, Pie, PieChart } from 'recharts';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { CertificateDownloadIconButton } from '~/backup/shared/CertificateDownloadIconButton';
import {
  PortalBusDealsQueryGQLType,
  usePortalBusDealsQuery,
} from '~/backup/shared/deals.gql.generated';
import {
  DEAL_STATUS_DISPLAY,
  DEAL_STATUSES,
  DealStatus,
  periodStatus,
  reportingPeriod,
} from '~/backup/shared/dealStatus';
import {
  formatDollars,
  formatDollarsCompact,
  formatPeriodShort,
  formatShare,
} from '~/backup/shared/format';
import { over30DpdPrincipal } from '~/backup/shared/portfolioMetrics';
import { todayFullDate } from '~/backup/shared/servicingPeriods';
import { Icon } from '~/packages/common-frontend/components/icons';

type Deal = PortalBusDealsQueryGQLType['portalBusDeals'][number];
type DealPeriod = Deal['periods'][number];
type Metrics = NonNullable<
  NonNullable<DealPeriod['cycle']>['portfolioMetrics']
>;

/** One row of the portfolio table: a deal and where it stands. */
type DealRow = {
  deal: Deal;
  /** The period the deal is reporting on; null before any is scheduled. */
  period: DealPeriod | null;
  status: DealStatus;
  /**
   * The newest reconciled tape's metrics and its period. Can be older than
   * `period`: a deal whose latest tape is overdue still has a book.
   */
  metrics: { reportingPeriod: string; value: Metrics } | null;
};

function toRow(deal: Deal, today: string): DealRow {
  const period = reportingPeriod(deal.periods, today);
  const metricsPeriod = deal.periods.find((p) => p.cycle?.portfolioMetrics);
  const value = metricsPeriod?.cycle?.portfolioMetrics ?? null;
  return {
    deal,
    period,
    status: period ? periodStatus(period, today) : 'awaitingTape',
    metrics:
      metricsPeriod && value
        ? { reportingPeriod: metricsPeriod.reportingPeriod, value }
        : null,
  };
}

type Totals = { principal: number; loans: number; over30Dpd: number };

function sumTotals(rows: DealRow[]): Totals {
  return rows.reduce<Totals>(
    (sum, { metrics }) =>
      metrics
        ? {
            principal: sum.principal + metrics.value.totalPrincipal,
            loans: sum.loans + metrics.value.loanCount,
            over30Dpd:
              sum.over30Dpd + over30DpdPrincipal(metrics.value.delinquency),
          }
        : sum,
    { principal: 0, loans: 0, over30Dpd: 0 },
  );
}

function StatusLabel({ status }: { status: DealStatus }): React.ReactElement {
  const { label, icon, color } = DEAL_STATUS_DISPLAY[status];
  return (
    <HStack spacing="0.5rem" align="center">
      <Icon icon={icon} type="solid" color={color} />
      <Text fontSize="0.8rem">{label}</Text>
    </HStack>
  );
}

function SummaryCard({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: string;
  subtitle: string | null;
}): React.ReactElement {
  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor={colors.gray[100]}
      p="1.5rem"
    >
      <VStack align="flex-start" spacing="0.25rem">
        <Text color={colors.gray[500]} fontSize="0.8rem" fontWeight={600}>
          {label}
        </Text>
        <Text fontWeight={600} fontSize="1.75rem">
          {value}
        </Text>
        {subtitle && (
          <Text color={colors.gray[500]} fontSize="0.8rem">
            {subtitle}
          </Text>
        )}
      </VStack>
    </Box>
  );
}

/**
 * How many deals sit in each status, as a donut whose legend is the table's
 * status filter: choosing a status shows only those deals, choosing it again
 * shows all of them.
 */
function StatusChart({
  rows,
  filter,
  onFilter,
}: {
  rows: DealRow[];
  filter: DealStatus | null;
  onFilter: (status: DealStatus | null) => void;
}): React.ReactElement {
  const counts = DEAL_STATUSES.map((status) => ({
    status,
    count: rows.filter((r) => r.status === status).length,
  })).filter(({ count }) => count > 0);

  return (
    <Flex
      borderWidth="1px"
      borderRadius="md"
      borderColor={colors.gray[100]}
      p="1.5rem"
      gap="1.5rem"
      align="center"
      gridColumn={{ base: 'auto', md: '1 / -1', xl: 'auto' }}
    >
      <PieChart width={140} height={140}>
        <Pie
          data={counts}
          dataKey="count"
          nameKey="status"
          innerRadius={42}
          outerRadius={66}
          isAnimationActive={false}
        >
          {counts.map(({ status }) => (
            <Cell
              key={status}
              fill={DEAL_STATUS_DISPLAY[status].color}
              opacity={filter === null || filter === status ? 1 : 0.3}
            />
          ))}
        </Pie>
      </PieChart>
      <VStack align="flex-start" spacing="0.25rem">
        <Text color={colors.gray[500]} fontSize="0.8rem" fontWeight={600}>
          Certificate status
        </Text>
        {counts.map(({ status, count }) => (
          <Button
            key={status}
            size="sm"
            variant={filter === status ? 'solid' : 'ghost'}
            fontWeight={filter === status ? 600 : 400}
            leftIcon={
              <Icon
                icon={DEAL_STATUS_DISPLAY[status].icon}
                type="solid"
                color={DEAL_STATUS_DISPLAY[status].color}
              />
            }
            aria-pressed={filter === status}
            onClick={(): void => onFilter(filter === status ? null : status)}
          >
            {DEAL_STATUS_DISPLAY[status].label} · {count}
          </Button>
        ))}
      </VStack>
    </Flex>
  );
}

function DealRowCells({ row }: { row: DealRow }): React.ReactElement {
  const { deal, period, status, metrics } = row;
  const cycle = period?.cycle ?? null;
  return (
    <Tr>
      <Td>
        <Link
          as={RouterLink}
          to={`/deals/${deal.implementationId}`}
          fontWeight={500}
          color={colors.blue[400]}
        >
          {deal.creditFacilityName}
        </Link>
        <Text fontSize="0.75rem" color={colors.gray[500]}>
          {deal.assetClass ?? deal.implementationName}
        </Text>
      </Td>
      <Td>
        <Text fontSize="0.8rem" fontWeight={600}>
          {period ? formatPeriodShort(period.reportingPeriod) : '—'}
        </Text>
        <StatusLabel status={status} />
      </Td>
      <Td isNumeric>
        <Text fontSize="0.8rem">
          {formatDollars(metrics?.value.totalPrincipal)}
        </Text>
        {/* Say when the book is from an earlier tape than the period shown. */}
        {metrics && metrics.reportingPeriod !== period?.reportingPeriod && (
          <Text fontSize="0.75rem" color={colors.gray[500]}>
            as of {formatPeriodShort(metrics.reportingPeriod)}
          </Text>
        )}
      </Td>
      <Td isNumeric>
        <Text fontSize="0.8rem">
          {metrics ? metrics.value.loanCount.toLocaleString() : '—'}
        </Text>
      </Td>
      <Td isNumeric>
        <Text fontSize="0.8rem">
          {metrics
            ? formatShare(
                over30DpdPrincipal(metrics.value.delinquency),
                metrics.value.totalPrincipal,
              )
            : '—'}
        </Text>
      </Td>
      <Td>
        {cycle?.certificateStatus === 'issued' ? (
          <CertificateDownloadIconButton
            servicingCycleId={cycle.id}
            label={`${deal.creditFacilityName} certificate`}
          />
        ) : (
          <Text fontSize="0.8rem" color={colors.gray[500]}>
            —
          </Text>
        )}
      </Td>
    </Tr>
  );
}

function PortfolioMessage({
  message,
  color,
}: {
  message: string;
  color: string;
}): React.ReactElement {
  return (
    <Flex direction="column" padding="2.5rem" gap="0.5rem">
      <Typography variant="page-title" trim>
        Portfolio Overview
      </Typography>
      <Text color={color}>{message}</Text>
    </Flex>
  );
}

export const PortfolioView = (): React.ReactElement => {
  const [filter, setFilter] = useState<DealStatus | null>(null);
  const { data, loading, error } = usePortalBusDealsQuery({
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Flex justify="center" align="center" minH="20rem">
        <Spinner />
      </Flex>
    );
  }

  if (error) {
    return (
      <PortfolioMessage
        message="Unable to load portfolio data. Please try again."
        color={colors.red[400]}
      />
    );
  }

  const today = todayFullDate();
  const rows = (data?.portalBusDeals ?? [])
    .map((deal) => toRow(deal, today))
    .sort((a, b) =>
      a.deal.creditFacilityName.localeCompare(b.deal.creditFacilityName),
    );

  if (rows.length === 0) {
    return (
      <PortfolioMessage
        message="No backup-servicing deals are available on your account yet."
        color={colors.gray[500]}
      />
    );
  }

  const totals = sumTotals(rows);
  const shown = filter ? rows.filter((r) => r.status === filter) : rows;
  const shownTotals = sumTotals(shown);

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="2rem"
    >
      <Typography variant="page-title" trim>
        Portfolio Overview
      </Typography>

      <Grid
        templateColumns={{
          base: '1fr',
          md: 'repeat(3, 1fr)',
          xl: 'repeat(3, 1fr) 1.5fr',
        }}
        gap="1rem"
      >
        <SummaryCard
          label="Total current principal"
          value={formatDollarsCompact(totals.principal)}
          subtitle={`${formatShare(
            totals.over30Dpd,
            totals.principal,
          )} over 30 days past due`}
        />
        {/* Every tape row, including zero-balance paid-off / charged-off loans. */}
        <SummaryCard
          label="Loans"
          value={totals.loans.toLocaleString()}
          subtitle="On each deal's latest tape"
        />
        <SummaryCard
          label="Deals"
          value={String(rows.length)}
          subtitle={null}
        />
        <StatusChart rows={rows} filter={filter} onFilter={setFilter} />
      </Grid>

      <TableContainer
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="md"
        whiteSpace="normal"
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Deal</Th>
              <Th>Period</Th>
              <Th isNumeric>Current Principal</Th>
              <Th isNumeric>Loans</Th>
              <Th isNumeric>30+ DPD</Th>
              <Th>Certificate</Th>
            </Tr>
          </Thead>
          <Tbody>
            {shown.map((row) => (
              <DealRowCells key={row.deal.implementationId} row={row} />
            ))}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th textTransform="none">
                {filter
                  ? `${DEAL_STATUS_DISPLAY[filter].label} (${shown.length} of ${rows.length})`
                  : `All deals (${rows.length})`}
              </Th>
              <Th />
              <Th isNumeric>{formatDollars(shownTotals.principal)}</Th>
              <Th isNumeric>{shownTotals.loans.toLocaleString()}</Th>
              <Th isNumeric>
                {formatShare(shownTotals.over30Dpd, shownTotals.principal)}
              </Th>
              <Th />
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
    </Flex>
  );
};
